import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { galleryCategories } from "@/data/gallery";
import { useLang, pick } from "@/lib/i18n";

const iconBtn = "absolute z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full";

export const GalleryFilters = ({ filter, onChange }) => {
  const { t } = useLang();
  return (
    <div role="tablist" aria-label="Gallery filters" className="flex flex-wrap gap-2 justify-center mb-10">
      {["all", ...galleryCategories].map((c) => (
        <button
          key={c}
          role="tab"
          aria-selected={filter === c}
          onClick={() => onChange(c)}
          className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-colors ${filter === c ? "bg-hacienda-terracotta border-hacienda-terracotta text-white" : "border-hacienda-border-light text-hacienda-charcoal hover:border-hacienda-terracotta"}`}
          data-testid={`gallery-filter-${c}`}
        >
          {c === "all" ? t("gallery.all") : t(`gallery.filters.${c}`)}
        </button>
      ))}
    </div>
  );
};

export const GalleryGrid = ({ images, onSelect }) => {
  const { t, lang } = useLang();
  return (
    <ul className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {images.map((img, i) => (
        <li key={img.src}>
          <button onClick={() => onSelect(i)} className="group relative w-full overflow-hidden rounded-2xl shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hacienda-terracotta" aria-label={`${pick(img.title, lang)} — ${t("actions.viewOptions")}`} data-testid={`gallery-image-${i}`}>
            <div className="aspect-square overflow-hidden">
              <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width="600" height="600" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-4 text-left">
              <p className="text-white font-serif text-xl font-semibold">{pick(img.title, lang)}</p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

export const Lightbox = ({ image, onClose, onPrev, onNext }) => {
  const { t, lang } = useLang();
  return (
    <Dialog open={!!image} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-transparent border-none" aria-describedby={undefined}>
        <DialogTitle className="sr-only">{image ? pick(image.title, lang) : ""}</DialogTitle>
        {image && (
          <div className="relative bg-hacienda-charcoal rounded-2xl overflow-hidden">
            <button onClick={onClose} className={`${iconBtn} top-3 right-3`} aria-label={t("actions.close")} data-testid="lightbox-close"><X size={22} /></button>
            <button onClick={onPrev} className={`${iconBtn} left-3 top-1/2 -translate-y-1/2`} aria-label="Previous image" data-testid="lightbox-prev"><ChevronLeft size={22} /></button>
            <button onClick={onNext} className={`${iconBtn} right-3 top-1/2 -translate-y-1/2`} aria-label="Next image" data-testid="lightbox-next"><ChevronRight size={22} /></button>
            <img src={image.src} alt={image.alt} className="w-full max-h-[70vh] object-contain" />
            <div className="p-5 text-center">
              <p className="text-white font-serif text-2xl font-semibold">{pick(image.title, lang)}</p>
              <p className="text-hacienda-cream/70 text-sm mt-1">{image.alt}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
